import { motion } from 'framer-motion'
import { ArrowRightIcon, CpuChipIcon, BeakerIcon, ChartBarIcon, LightBulbIcon, CodeBracketIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import styles from './InteligenciaArtificial.module.css'

// Icono personalizado para AI
const AIIcon = ({ className, animate = false }: { className?: string; animate?: boolean }) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    animate={animate ? {
      rotate: [0, 360],
      scale: [1, 1.1, 1],
    } : {}}
    transition={animate ? {
      duration: 3,
      repeat: Infinity,
      ease: "linear"
    } : {}}
  >
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
  </motion.svg>
)

export default function InteligenciaArtificial() {
  const features = [
    {
      icon: CpuChipIcon,
      title: "Machine Learning",
      description: "Algoritmos de aprendizaje automático para análisis predictivo y optimización"
    },
    {
      icon: BeakerIcon,
      title: "Deep Learning",
      description: "Redes neuronales profundas para reconocimiento de patrones complejos"
    },
    {
      icon: ChartBarIcon,
      title: "Análisis de Datos",
      description: "Procesamiento inteligente de grandes volúmenes de datos"
    },
    {
      icon: LightBulbIcon,
      title: "Automatización IA",
      description: "Automatización inteligente de procesos empresariales"
    }
  ]

  const technologies = [
    { name: "TensorFlow", category: "Framework" },
    { name: "PyTorch", category: "Framework" },
    { name: "OpenAI GPT", category: "LLM" },
    { name: "Scikit-learn", category: "ML Library" },
    { name: "Pandas", category: "Data" },
    { name: "NumPy", category: "Computing" },
    { name: "Keras", category: "Neural Networks" },
    { name: "Python", category: "Language" }
  ]

  const useCases = [
    {
      title: "Chatbots Inteligentes",
      description: "Asistentes virtuales con procesamiento de lenguaje natural",
      impact: "85% reducción en tiempo de respuesta"
    },
    {
      title: "Análisis Predictivo",
      description: "Predicción de tendencias y comportamientos futuros",
      impact: "40% mejora en precisión"
    },
    {
      title: "Visión por Computadora",
      description: "Reconocimiento y análisis automático de imágenes",
      impact: "95% precisión en detección"
    },
    {
      title: "Automatización de Procesos",
      description: "Optimización inteligente de flujos de trabajo",
      impact: "60% reducción de tiempo manual"
    }
  ]

  const stats = [
    {
      metric: "95%",
      label: "Precisión en modelos"
    },
    {
      metric: "50%",
      label: "Reducción de costos"
    },
    {
      metric: "24/7",
      label: "Disponibilidad"
    },
    {
      metric: "10x",
      label: "Velocidad de procesamiento"
    }
  ]

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        {/* Background Elements */}
        <div className={styles.backgroundElements}>
          <div className={styles.backgroundBlur1} />
          <div className={styles.backgroundBlur2} />
        </div>

        {/* Neural Network Animation */}
        <div className={styles.neuralNetwork}>
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className={styles.neuralNode}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [1, 2, 1],
                opacity: [0.3, 1, 0.3],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className={styles.heroContent}>
          <div className={styles.heroGrid}>
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className={styles.badge}
              >
                Inteligencia Artificial
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={styles.heroTitle}
              >
                IA que{' '}
                <span className={styles.titleGradient}>
                  transforma
                </span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className={styles.heroDescription}
              >
                Implementamos soluciones de inteligencia artificial que automatizan 
                procesos, optimizan decisiones y crean experiencias extraordinarias.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className={styles.ctaButtons}
              >
                <Link to="/contacto" className={styles.primaryButton}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    Consulta Gratuita
                    <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                  </motion.div>
                </Link>
                <Link to="/casos" className={styles.secondaryButton}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Ver Casos de IA
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={styles.visualContainer}
            >
              <div style={{ position: 'relative' }}>
                {/* Central Brain */}
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className={styles.centralBrain}
                >
                  <div className={styles.aiIcon}>
                    <AIIcon animate={true} />
                  </div>
                </motion.div>

                {/* Orbiting Tech Icons */}
                {[
                  { icon: CpuChipIcon, rotation: 0, radius: 150 },
                  { icon: BeakerIcon, rotation: 72, radius: 150 },
                  { icon: ChartBarIcon, rotation: 144, radius: 150 },
                  { icon: LightBulbIcon, rotation: 216, radius: 150 },
                  { icon: CodeBracketIcon, rotation: 288, radius: 150 }
                ].map((element, index) => (
                  <motion.div
                    key={index}
                    className={styles.orbitingIcon}
                    animate={{
                      rotate: element.rotation + 360
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <motion.div
                      className={styles.orbitingIconInner}
                      style={{
                        transform: `translateY(-${element.radius}px)`
                      }}
                      animate={{
                        rotate: -(element.rotation + 360)
                      }}
                      transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    >
                      <element.icon className={styles.orbitingIconSvg} />
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.sectionHeader}
          >
            <h2 className={styles.sectionTitle}>
              Capacidades de IA
            </h2>
            <p className={styles.sectionDescription}>
              Desarrollamos soluciones de inteligencia artificial personalizadas 
              que se adaptan a las necesidades específicas de tu empresa
            </p>
          </motion.div>

          <div className={styles.featuresGrid}>
            {features.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  className={styles.featureCard}
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className={styles.featureIcon}
                  >
                    <IconComponent className={styles.featureIconSvg} />
                  </motion.div>
                  <h3 className={styles.featureTitle}>
                    {feature.title}
                  </h3>
                  <p className={styles.featureDescription}>
                    {feature.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className={styles.sectionAlternate}>
        <div className={styles.sectionContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.sectionHeader}
          >
            <h2 className={styles.sectionTitle}>
              Tecnologías de IA
            </h2>
            <p className={styles.sectionDescription}>
              Utilizamos las herramientas más avanzadas del ecosistema de IA
            </p>
          </motion.div>

          <div className={styles.technologiesGrid}>
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className={styles.technologyCard}
              >
                <h4 className={styles.technologyName}>
                  {tech.name}
                </h4>
                <span className={styles.technologyCategory}>
                  {tech.category}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.sectionHeader}
          >
            <h2 className={styles.sectionTitle}>
              Casos de Uso
            </h2>
            <p className={styles.sectionDescription}>
              Aplicaciones reales de IA que están transformando empresas
            </p>
          </motion.div>

          <div className={styles.useCasesGrid}>
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className={styles.useCaseCard}
              >
                <h3 className={styles.useCaseTitle}>
                  {useCase.title}
                </h3>
                <p className={styles.useCaseDescription}>
                  {useCase.description}
                </p>
                <div className={styles.useCaseImpact}>
                  {useCase.impact}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.sectionAlternate}>
        <div className={styles.sectionContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.sectionHeader}
          >
            <h2 className={styles.sectionTitle}>
              Resultados Comprobados
            </h2>
            <p className={styles.sectionDescription}>
              Nuestras implementaciones de IA entregan resultados medibles
            </p>
          </motion.div>

          <div className={styles.statsGrid}>
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={styles.statCard}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={styles.statMetric}
                >
                  {stat.metric}
                </motion.div>
                <p className={styles.statLabel}>
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContainer}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.ctaCard}
          >
            <h2 className={styles.ctaTitle}>
              ¿Listo para la IA?
            </h2>
            <p className={styles.ctaDescription}>
              Descubre cómo la inteligencia artificial puede transformar tu empresa
            </p>
            
            <div className={styles.ctaButtons}>
              <Link to="/contacto" className={styles.primaryButton}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  Consulta de IA
                  <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                </motion.div>
              </Link>
              <Link to="/casos" className={styles.secondaryButton}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Ver Proyectos de IA
                </motion.div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
